import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Badge,
  Card,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import {
  IconAdjustments,
  IconBell,
  IconChevronRight,
  IconDeviceMobile,
  IconKey,
  IconLock,
  IconMoon,
  IconPlug,
  IconShieldCheck,
  IconSun,
  IconUser,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useMantineColorScheme } from '@mantine/core';
import { DARK_THEME_ENABLED } from '@/shared/lib/temaScuro';
import * as devicesApi from '@/features/users/devices';
import { listApiKeys } from '@/features/api-keys/api';
import { useCurrentUserId } from '@/shared/hooks/useCurrentUserId';
import { useAuth } from '@/app/providers/AuthProvider';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { mw, mwSectionLabel } from '@/shared/mobile/tokens';
import { useCanManageApiKeys } from '@/features/api-keys/useCanManageApiKeys';

export const Route = createFileRoute('/_app/settings/')({ component: SettingsIndex });

interface CardDef {
  to: string;
  label: string;
  desc: string;
  icon: typeof IconUser;
  color: string;
  /** badge live, computed via React in the cell */
  badgeKey: 'profile' | 'devices' | 'apiKeys' | 'theme' | 'notifications' | 'security' | 'privacy' | 'integrations' | 'preferences';
}

const CARDS: CardDef[] = [
  { to: '/settings/profile', label: 'Profilo', desc: 'Anagrafica e avatar', icon: IconUser, color: 'blue', badgeKey: 'profile' },
  { to: '/settings/preferences', label: 'Preferenze', desc: 'Lingua, tema, fuso orario', icon: IconAdjustments, color: 'teal', badgeKey: 'preferences' },
  { to: '/settings/notification-preferences', label: 'Notifiche', desc: 'Canali e categorie', icon: IconBell, color: 'cyan', badgeKey: 'notifications' },
  { to: '/settings/security', label: 'Sicurezza', desc: 'Password e accessi', icon: IconLock, color: 'orange', badgeKey: 'security' },
  { to: '/settings/devices', label: 'Dispositivi', desc: 'Sessioni e device attivi', icon: IconDeviceMobile, color: 'grape', badgeKey: 'devices' },
  { to: '/settings/api-keys', label: 'API Keys', desc: 'Token integrazione', icon: IconKey, color: 'yellow', badgeKey: 'apiKeys' },
  { to: '/settings/integrations', label: 'Integrazioni', desc: 'Webhook e servizi esterni', icon: IconPlug, color: 'indigo', badgeKey: 'integrations' },
  { to: '/settings/privacy', label: 'Privacy / GDPR', desc: 'Export dati e cancellazione', icon: IconShieldCheck, color: 'red', badgeKey: 'privacy' },
];

/** Raggruppamento delle sezioni per la lista mobile (per "gruppi comuni"). */
const MOBILE_GROUPS: { title: string; keys: string[] }[] = [
  { title: 'Account', keys: ['/settings/profile', '/settings/preferences', '/settings/notification-preferences'] },
  { title: 'Sicurezza & dispositivi', keys: ['/settings/security', '/settings/devices'] },
  { title: 'Sviluppo & dati', keys: ['/settings/api-keys', '/settings/integrations', '/settings/privacy'] },
];

function SettingsIndex() {
  const userId = useCurrentUserId();
  const { user } = useAuth();
  const { colorScheme } = useMantineColorScheme();
  const isMobile = useIsMobileShell();

  const devicesQ = useQuery({
    queryKey: ['my-devices', userId],
    queryFn: () => devicesApi.fetchMyDevices(userId),
    enabled: Boolean(userId),
    staleTime: 60_000,
  });

  const canApiKeys = useCanManageApiKeys();
  const cards = CARDS.filter((c) => c.to !== '/settings/api-keys' || canApiKeys);
  const apiKeysQ = useQuery({
    queryKey: ['api-keys'],
    queryFn: listApiKeys,
    enabled: canApiKeys,
    staleTime: 60_000,
  });

  const badgeFor = (key: CardDef['badgeKey']): React.ReactNode => {
    switch (key) {
      case 'profile':
        return user?.email ? (
          <Badge variant="light" size="xs" color="gray">{user.email}</Badge>
        ) : null;
      case 'devices': {
        if (devicesQ.isLoading) return <Loader size={12} />;
        if (devicesQ.isError) return <QueryErrorAlert error={devicesQ.error} onRetry={() => { void devicesQ.refetch(); }} />;
        const n = (devicesQ.data as unknown as { data?: unknown[] })?.data?.length
          ?? (Array.isArray(devicesQ.data) ? devicesQ.data.length : 0);
        return (
          <Badge variant="light" size="xs" color={n > 0 ? 'grape' : 'gray'}>
            {n} {n === 1 ? 'device' : 'device'}
          </Badge>
        );
      }
      case 'apiKeys': {
        if (apiKeysQ.isLoading) return <Loader size={12} />;
        if (apiKeysQ.isError) return <QueryErrorAlert error={apiKeysQ.error} onRetry={() => { void apiKeysQ.refetch(); }} />;
        const n = Array.isArray(apiKeysQ.data) ? apiKeysQ.data.length : 0;
        return (
          <Badge variant="light" size="xs" color={n > 0 ? 'yellow' : 'gray'}>
            {n} attive
          </Badge>
        );
      }
      case 'preferences':
        /*
         * Nessun badge finche il tema scuro e sospeso. Non e solo una voce che avanza: qui si
         * legge `colorScheme`, cioe il valore **dichiarato**, non quello a schermo — con il
         * provider che forza il chiaro il badge avrebbe potuto annunciare «Dark» su un
         * applicativo chiaro. Vedi `temaScuro.ts`.
         */
        if (!DARK_THEME_ENABLED) return null;
        return (
          <Badge
            variant="light"
            size="xs"
            color="teal"
            leftSection={colorScheme === 'dark' ? <IconMoon size={10} /> : <IconSun size={10} />}
          >
            {colorScheme === 'dark' ? 'Dark' : colorScheme === 'light' ? 'Light' : 'Auto'}
          </Badge>
        );
      case 'notifications':
        return <Badge variant="light" size="xs" color="cyan">Configura</Badge>;
      case 'security':
        return <Badge variant="light" size="xs" color="orange">2FA off</Badge>;
      case 'privacy':
        return <Badge variant="light" size="xs" color="red">GDPR</Badge>;
      case 'integrations':
        return <Badge variant="light" size="xs" color="indigo">Webhook</Badge>;
      default:
        return null;
    }
  };

  if (isMobile) {
    const byTo = new Map(cards.map((c) => [c.to, c]));
    return (
      <Stack gap={18}>
        {MOBILE_GROUPS.map((g) => (
          <div key={g.title}>
            <Text style={{ ...mwSectionLabel, marginBottom: 8 }}>{g.title}</Text>
            {g.keys.map((k) => {
              const c = byTo.get(k);
              if (!c) return null;
              return (
                <Link key={c.to} to={c.to} style={{ textDecoration: 'none' }}>
                  <div
                    className="mw-pressable"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8,
                      border: `1px solid ${mw.line}`, borderRadius: mw.radiusTile,
                      background: mw.surface, padding: '12px 14px',
                    }}
                  >
                    <ThemeIcon size={42} radius="md" variant="light" color={c.color}>
                      <c.icon size={22} />
                    </ThemeIcon>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text fw={600} style={{ color: mw.ink900, fontSize: 'var(--fs-body-lg)', lineHeight: 1.2 }}>{c.label}</Text>
                      <Text size="xs" c="dimmed">{c.desc}</Text>
                    </div>
                    <span style={{ flexShrink: 0, maxWidth: 130, overflow: 'hidden', display: 'flex' }}>
                      {badgeFor(c.badgeKey)}
                    </span>
                    <IconChevronRight size={18} style={{ color: mw.ink300, flexShrink: 0 }} />
                  </div>
                </Link>
              );
            })}
          </div>
        ))}
      </Stack>
    );
  }

  return (
    <>
      <Text style={{ ...mwSectionLabel, marginBottom: 12 }}>Panoramica</Text>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        {cards.map((c) => (
          <Card
            key={c.to}
            withBorder
            padding="lg"
            radius="lg"
            shadow="sm"
            component={Link}
            to={c.to}
            style={{ textDecoration: 'none' }}
          >
            <Group justify="space-between" mb="sm">
              <ThemeIcon size="xl" radius="md" variant="light" color={c.color}>
                <c.icon size={26} />
              </ThemeIcon>
              {badgeFor(c.badgeKey)}
            </Group>
            <Text fw={600} c="var(--mantine-color-text)">{c.label}</Text>
            <Text size="sm" c="dimmed" mt={2}>{c.desc}</Text>
          </Card>
        ))}
      </SimpleGrid>
    </>
  );
}
